import React, { useMemo } from 'react'
import { CircularProgress } from '@mui/material'
import CustomDialog from '../../../components/CustomDialog'
import betDetailsModalData from '../../../utils/betDetailsModalData'

const formatDateTime = (value) => {
  if (!value) return 'TBD'
  const date = new Date(value)
  if (!Number.isFinite(date.valueOf())) return value

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

const BetDetailsModal = ({
  open,
  onClose,
  ticketId,
  events = [],
  isLoading = false,
  error = null,
  onRetry,
}) => {
  const hydratedEvents = useMemo(() => {
    if (!Array.isArray(events)) return []
    return events.slice(0, 12)
  }, [events])

  const hasHydratedContent = hydratedEvents.length > 0
  const eventsToRender = hasHydratedContent ? hydratedEvents : events
  //const eventsToRender = betDetailsModalData

  return (
    <CustomDialog
      open={open}
      width={560}
      padding={0}
      extraStyle={{ borderRadius: 8, overflow: 'hidden', padding: 0 }}
    >
      <div className="flex flex-col bg-white text-customPrimaryText dark:bg-customSurface dark:text-customHeading">
        <header className="flex items-start justify-between border-b border-customPredawn/10 bg-customSlate/80 px-6 py-5 dark:border-customSurfaceMuted dark:bg-custom_dark_primary2/80">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-customAccent dark:text-customMuted">
              Ticket overview
            </p>
            <h2 className="mt-1 text-xl font-semibold">Voucher {ticketId ?? '-'}</h2>
            <p className="text-xs text-customSecondaryText dark:text-customMuted">
              Review every leg including kick-off, result, and chosen team.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="bg-white rounded-full border border-transparent px-3 py-1 text-xs font-semibold uppercase tracking-wide text-customPrimaryText transition hover:bg-slate-300/20 dark:text-customHeading dark:hover:bg-customSurfaceMuted/40"
          >
            Close
          </button>
        </header>

        <section className="max-h-[70vh] overflow-y-auto px-4 py-3">
          {
            isLoading ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-customSecondaryText dark:text-customMuted">
                <CircularProgress size={28} thickness={5} />
                <span>Fetching the latest event breakdown...</span>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-center text-sm text-customFontRed">
                <span>We could not load the ticket details right now.</span>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => onRetry?.()}
                    className="rounded-xl bg-customPredawn px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-sm transition hover:bg-customAccentBlue/90"
                  >
                    Try again
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-xl border border-customPredawn/30 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-customPrimaryText transition hover:border-customPredawn/50 hover:bg-customPredawn/10 dark:border-customSurfaceMuted dark:text-customHeading"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) :
              eventsToRender.length ? (
                <div className="grid gap-2">
                  {!hasHydratedContent ? (
                    <p className="rounded-lg bg-customSlate/30 px-4 py-2 text-xs text-customSecondaryText dark:bg-custom_dark_primary2/50 dark:text-customMuted">
                      Showing sample ticket data while live voucher details load.
                    </p>
                  ) : null}
                  {eventsToRender.map((event, index) => (
                    <article
                      key={`${event.league ?? 'event'}-${event.home ?? index}`}
                      className="rounded-2xl border border-customPredawn/10 bg-white/80 px-4 py-2 shadow-sm shadow-customAccentBlue/10 transition hover:border-customPredawn/30 dark:border-customSurfaceMuted dark:bg-customSurfaceMuted/40 dark:shadow-none"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-widest text-customAccent dark:text-customMuted">
                            Leg {index + 1}
                          </p>
                          <h3 className="text-sm font-semibold text-customPrimaryText dark:text-customHeading">
                            {event.league ?? 'Unknown league'}
                          </h3>
                          <p className="text-xs text-customSecondaryText dark:text-customMuted">
                            Event time - {formatDateTime(event.eventTime)}
                          </p>
                        </div>
                        <span className="rounded-full bg-customVibrantGreen px-3 py-1 text-xs font-semibold uppercase tracking-wide text-customLightMilkWhite dark:bg-customAccent/20 dark:text-customHeading">
                          {event.result ?? 'Pending'} 
                        </span>
                      </div>

                      <dl className="mt-4 grid gap-1 text-xs text-customPrimaryText dark:text-customHeading">
                        <div className="flex items-center justify-between rounded-xl bg-customSlate/60 px-3 py-2 dark:bg-custom_dark_primary2/60">
                          <dt className="font-semibold text-customSecondaryText dark:text-customMuted">{event.home ?? '-'} vs {event.away ?? '-'}</dt>
                          <dd className="font-semibold">{event.odds ?? '-'}</dd>
                        </div>
                        {/* <div className="flex items-center justify-between rounded-xl bg-customSlate/50 px-3 py-2 dark:bg-custom_dark_primary2/60">
                      <dt className="font-semibold text-customSecondaryText dark:text-customMuted">Away</dt>
                      <dd className="font-semibold">{event.away ?? '-'}</dd>
                    </div> */}
                        <div className="flex items-center justify-between rounded-xl bg-customSlate/40 px-3 py-2 dark:bg-custom_dark_primary2/40">
                          <dt className="font-semibold text-customSecondaryText dark:text-customMuted">Betted team</dt>
                          <dd className={`font-semibold ${event.bettedColor === 'red' ? 'text-customFontRed' : 'text-customDarkGreen'}`}>
                            {event.bettedTeam ?? '-'}
                          </dd>
                        </div>
                      </dl>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-customSecondaryText dark:text-customMuted">
                  <span>No event details available for this ticket.</span>
                </div>
              )}
        </section>
      </div>
    </CustomDialog>
  )
}

export default BetDetailsModal
